import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"
import { TranslationProvider } from "./i18n"
import Navbar from "./components/Navbar"
import Footer from "./components/Footer"
import AdminRoute from "./components/AdminRoute"
import HomePage from "./pages/HomePage"
import LoginPage from "./pages/auth/LoginPage"
import SignupPage from "./pages/auth/SignupPage"
import VerifyEmailPage from "./pages/auth/VerifyEmailPage"
import ResetPasswordPage from "./pages/auth/ResetPasswordPage"
import ProfileUserPage from "./pages/profile/ProfileUserPage"
import AdminDashboard from "./pages/admin/AdminDashboard"
import UserManagementPage from "./pages/admin/UserManagementPage"
import CourseManagementPage from "./pages/admin/CourseManagementPage"
import CreateCoursePage from "./pages/admin/CreateCoursePage"
import EditCoursePage from "./pages/admin/EditCoursePage"
import AdvancedCourseEditorPage from "./pages/admin/AdvancedCourseEditorPage"
import AdminManagementPage from "./pages/admin/AdminManagementPage"
import CommentsManagementPage from "./pages/admin/CommentsManagementPage"
import PaymentsPage from "./pages/admin/PaymentsPage"
import WebinarManagementPage from "./pages/admin/WebinarManagementPage"
import CreateWebinarPage from "./pages/admin/CreateWebinarPage"
import EditWebinarPage from "./pages/admin/EditWebinarPage"
import WebinarRegistrationsPage from "./pages/admin/WebinarRegistrationsPage"
import PromoCodesManagementPage from "./pages/admin/PromoCodesManagementPage"
import CourseEnrollmentsPage from "./pages/admin/CourseEnrollmentsPage"
import CategoriesManagementPage from "./pages/admin/CategoriesManagementPage"
import FAQManagementPage from "./pages/admin/FAQManagementPage"
import NewsletterSubscribersPage from "./pages/admin/NewsletterSubscribersPage"
import NewsletterCampaignsPage from "./pages/admin/NewsletterCampaignsPage"
import TestimonialsManagementPage from "./pages/admin/TestimonialsManagementPage"
import AboutPage from "./pages/AboutPage"
import CoursesPage from "./pages/CoursesPage"
import CourseDetailPage from "./pages/CourseDetailPage"
import CourseReaderPage from "./pages/CourseReaderPage"
import QuizPage from "./pages/QuizPage"
import ContactPage from "./pages/ContactPage"
import FAQPage from "./pages/FAQPage"
import MaintenancePage from "./pages/MaintenancePage"
import WebinarsPage from "./pages/WebinarsPage"
import WebinarDetailPage from "./pages/WebinarDetailPage"
import MyWebinarsPage from "./pages/MyWebinarsPage"
import { trackPageView } from "./config/analytics"
import "./index.css"

function AppContent() {
  const location = useLocation()
  const [navKey, setNavKey] = useState(0)
  const isVerifyEmail = location.pathname === '/verify-email'
  const isResetPassword = location.pathname === '/reset-password'

  // Corriger la page blanche au retour arrière (bfcache)
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        setNavKey((k) => k + 1)
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  // Remettre le scroll en haut à chaque changement de page
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Tracker les vues de pages avec Google Analytics
  useEffect(() => {
    trackPageView(location.pathname, document.title)
  }, [location.pathname])

  if (isVerifyEmail) {
    return <VerifyEmailPage />
  }

  if (isResetPassword) {
    // Page de réinitialisation de mot de passe sans navbar/footer
    console.log("Rendering ResetPasswordPage without navbar/footer")
    return <ResetPasswordPage />
  }

  // Vérifier si c'est une route admin ou de lecture de cours
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isCourseReaderRoute = location.pathname.includes('/courses/') && location.pathname.includes('/modules/')

  if (isAdminRoute) {
    // Routes admin protégées - sans navbar/footer
    return (
      <AdminRoute>
        <Routes>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagementPage />} />
          <Route path="/admin/courses" element={<CourseManagementPage />} />
          <Route path="/admin/courses/create" element={<CreateCoursePage />} />
          <Route path="/admin/courses/:id/edit" element={<EditCoursePage />} />
          <Route path="/admin/courses/new/editor" element={<AdvancedCourseEditorPage />} />
          <Route path="/admin/courses/:id/editor" element={<AdvancedCourseEditorPage />} />
          <Route path="/admin/course-enrollments" element={<CourseEnrollmentsPage />} />
          <Route path="/admin/categories" element={<CategoriesManagementPage />} />
          <Route path="/admin/promo-codes" element={<PromoCodesManagementPage />} />
          <Route path="/admin/admins" element={<AdminManagementPage />} />
          <Route path="/admin/comments" element={<CommentsManagementPage />} />
          <Route path="/admin/payments" element={<PaymentsPage />} />
          <Route path="/admin/webinars" element={<WebinarManagementPage />} />
          <Route path="/admin/webinars/create" element={<CreateWebinarPage />} />
          <Route path="/admin/webinars/:webinarId/edit" element={<EditWebinarPage />} />
          <Route path="/admin/webinars/:webinarId/registrations" element={<WebinarRegistrationsPage />} />
          <Route path="/admin/faqs" element={<FAQManagementPage />} />
          <Route path="/admin/newsletter/subscribers" element={<NewsletterSubscribersPage />} />
          <Route path="/admin/newsletter/campaigns" element={<NewsletterCampaignsPage />} />
          <Route path="/admin/testimonials" element={<TestimonialsManagementPage />} />
        </Routes>
      </AdminRoute>
    )
  }

  return (
    <>
      <Navbar />
      <Routes key={`${location.pathname}-${navKey}`}>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/profile" element={<ProfileUserPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/courses/:courseId" element={<CourseDetailPage />} />
        <Route path="/courses/:courseId/modules/:moduleId" element={<CourseReaderPage />} />
        <Route path="/courses/:courseId/modules/:moduleId/lessons/:lessonId" element={<CourseReaderPage />} />
        <Route path="/courses/:courseId/modules/:moduleId/lessons/:lessonId/blocks/:blockId" element={<CourseReaderPage />} />
        <Route path="/courses/:courseId/modules/:moduleId/blocks/:blockId" element={<CourseReaderPage />} />
        <Route path="/courses/:courseId/modules/:moduleId/quiz" element={<QuizPage />} />
        <Route path="/webinars" element={<WebinarsPage />} />
        <Route path="/webinars/:webinarId" element={<WebinarDetailPage />} />
        <Route path="/my-webinars" element={<MyWebinarsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
      </Routes>
      {!isCourseReaderRoute && <Footer />}
    </>
  )
}

function App() {
  return (
    <TranslationProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </TranslationProvider>
  )
}

export default App
